<template>
  <div class="p-grid">
    <div class="p-col-12">
      <div class="card">
        <Toolbar class="p-mb-4 p-p-2 page-toolbar">
          <template #start>
            <div style="font-size: 1.4em; color: inherit" class="p-p-2">{{ $t('Archived Orders') }}</div>
          </template>
          <template #end>
            <SearchInput :disabled="dataTableIsLoading" @change-processed-search-value="changeProcessedSearchValue" v-model="searchData" @show-spinner="isLoading = true"/>
          </template>
        </Toolbar>

        <Spinner v-if="isLoading"/>
        <DesktopDataTable v-else
                          :dataTableIsLoading="dataTableIsLoading"
                          :showHeader="false"
                          :itemsArrayIsEmpty="itemsArrayIsEmpty"
                          :sort="sort"
                          :showSearchInput="true"
                          :actionButtonsCount="actionButtonsCount"
                          @sort-items="sortItems"
                          :headers="[
                    {name: 'number', title: $t('Number'), minWidth: '94px', sortable: true},
                    {name: 'start', title: $t('Start'), sortable: false},
                    {name: 'customer', title: $t('Customer'), width: '14%', minWidth: '190px', sortable: false},
                    {name: 'order', title: $t('Name'), sortable: false},
                     {name: 'time', title: $t('Time'), sortable: false},
                    {name: 'car', title: $store.getters.isForkliftTenant ? $t('Equipment') : $t('Car'), width: '15%', sortable: false},
                     {name: 'masters', title: $t('techniciansShortLabel'), sortable: false},
                    {name: 'invoice', title: $t('Invoice'), sortable: false},
                    {name: 'grand_total', title: $t('Sum'), sortable: false},
                    {name: 'state', title: $t('Status'), sortable: false},
                    {name: 'archived_at', title: $t('archivedLabel'), width: '8%', minWidth: '136px', sortable: true},
                ]">
          <template v-slot:body>
            <template  v-for="(item, index) of items" :key="index">
              <tr :class="{'updated-item': isItemUpdated(item.id)}">
                <td>
                  <div>{{ item.number }}</div>
                  <div v-if="item.parentOffer && item.parentOffer.number && item.parentOffer.uid" class="p-pt-1" style="font-size: 0.85em">
                    <router-link :to="{ path: '/estimates', query: { show: item.parentOffer.uid } }"
                                 draggable="false"
                                 :target="settings.openAllLinksInSameTab ? '_self' : '_blank'"
                                 rel="noopener">
                      <i style="font-size: 0.90em; margin-right: 2px" class="ti-clipboard"></i>
                      <span>{{ item.parentOffer.number }}</span>
                    </router-link>
                  </div>
                  <div v-else-if="item.parentOrder && item.parentOrder.number && item.parentOrder.uid" class="p-pt-1" style="font-size: 0.85em">
                    <router-link :to="{ path: `/orders/${item.parentOrder.uid}` }"
                                 draggable="false"
                                 :target="settings.openAllLinksInSameTab ? '_self' : '_blank'"
                                 rel="noopener">
                      <i style="font-size: 0.90em" class="ti-hummer"></i>
                      <span>{{ item.parentOrder.number }}</span>
                    </router-link>
                  </div>
                </td>
                <td>
                  <span v-if="item.start">
                    <div>{{ formatDay(item.start) }}</div>
                    <div>{{ formatTime(item.start) }}</div>
                  </span>
                </td>
                <td>
                  <TableCustomerData :customer="item.customer"
                                     :contactPerson="item.contactPerson"
                                     :showPhoneNumber="false"
                                     @change-customer-status="changeCustomerAndContactPersonStatus"/>
                </td>
                <td>
                  <div>
                    <a href="#" draggable="false" @click.prevent="toggleShowPositionTable(item, false, true)">
                      <i style="font-size: 0.9em" class="p-mr-1 pi" :class="item.showPositionsTable ? 'pi-minus': 'pi-plus'"></i>
                      <span>{{ item.name || item.number }}</span>
                    </a>
                  </div>
                  <div class="table-small-text">
                    <span :class="{'created-by-customer':item.source === 2}">
                      {{ formatBy(item.created_at, item.creator) }}
                    </span>
                  </div>
                </td>

                <td>
                  <span v-if="hasTime(item.time)">{{ formatHoursAndMinutes(item.time) }} </span>
                  <i v-else class="ti-minus"/>
                </td>
                <td>
                  <span v-if="item.car && item.car.uid">
                    <router-link :to="{ path: `/vehicles/${item.car.uid}` }"
                                 draggable="false"
                                 :target="settings.openAllLinksInSameTab ? '_self' : '_blank'"
                                 rel="noopener">
                      <i v-if="!$store.getters.isForkliftTenant" class="ti-car p-mr-1"/>
                      <span>{{ carDisplayName(item.car) }}</span>
                    </router-link>
                    <div class="table-small-text p-d-flex p-ai-center">
                      <div>
                        <span v-if="carIdentifier(item.car)">{{ carIdentifier(item.car) }}</span>
                        <i v-else class="ti-minus"/>
                        <span v-if="item.car.year">, {{ item.car.year }}</span>
                      </div>
                      <CarDataLink v-if="item.car.plate_number"
                                   :plateNumber="item.car.plate_number"
                                   :showOnlyIcon="true"/>
                    </div>
                  </span>
                  <i v-else class="ti-minus"/>
                </td>

                <td class="avatar-td">
                  <AvatarsCell :masters="item.masters"/>
                </td>
                <td>
                  <template v-if="!$store.getters.isAccountantOrHigher && item.invoiceNumberAndUid?.number">
                    {{ item.invoiceNumberAndUid.number }}
                  </template>
                  <router-link v-else-if="item.invoiceNumberAndUid && item.invoiceNumberAndUid.number && item.invoiceNumberAndUid.uid"
                               :to="{ path: '/sales-invoices', query: { show: item.invoiceNumberAndUid.uid } }"
                               :target="settings.openAllLinksInSameTab ? '_self' : '_blank'"
                               rel="noopener">
                    <span>{{ item.invoiceNumberAndUid.number }}</span>
                  </router-link>
                  <i v-else class="ti-minus"/>
                </td>
                <td>
                  <span>{{ formatCurrency(item.grand_total) }}</span>
                  <div v-if="(item.state === 7 || item.state === 8) && +item.grand_total" style="font-size: 0.8rem" class="orange-color">
                    <span v-if="Number(item.unpaid) !== 0" class="orange-color">{{ $t('Due') }} {{ formatCurrency(item.unpaid) }}</span>
                    <span v-else-if="!item.is_archived" class="green-color">{{ $t('Paid') }}</span>
                  </div>
                </td>
                <td>
                  <OrderStateButton :item="item"/>
                </td>
                <td class="table-date-line">
                  <span v-if="item.is_archived">
                    {{ formatBy(item.archived_at, item.archivedBy) }}
                  </span>
                  <i v-else class="ti-minus"/>
                </td>

                <td>
                  <div class="actions">
                    <router-link :to="{ path: `/orders/${item.uid}` }" draggable="false">
                      <ViewButton/>
                    </router-link>
                  </div>
                </td>
              </tr>
              <tr v-show="item.showPositionsTable" class="table-opened-info">
                <td colspan="20" style="padding:0!important;">
                  <PositionsTable :item="item" :isSpoilerTable="true" @update-items="updateItems"/>
                </td>
              </tr>
            </template>
          </template>
        </DesktopDataTable>

        <OwnPaginator v-show="!itemsArrayIsEmpty"
                      :rows="pagination.perPage"
                      :currentPage="pagination.currentPage - 1"
                      :totalPages="pagination.pageCount"
                      :totalRecords="pagination.totalCount"
                      :rowsPerPageOptions="[5,10,25,50,100]"
                      :pageLinkSize="11">
        </OwnPaginator>

        <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>
      </div>

    </div>
  </div>
</template>

<script>
import httpClient from '@/services/http.services'
import OrderStateButton from '@/pages/orders/components/OrderStateButton'
import formatMixins from '@/mixins/formatMixins'
import PositionsTable from '@/components/PositionsTable'
import datatableMixins from '@/mixins/datatableMixins'
import httpMixins from "@/mixins/httpMixins";
import TableCustomerData from "@/components/TableCustomerData";
import settings from "@/settings";
import AvatarsCell from "@/components/AvatarsCell";
import CarDataLink from "@/pages/vehicles/components/CarDataLink";
import showErrorsMixins from "@/mixins/showErrorsMixins";

export default {
  mixins: [ formatMixins, datatableMixins, httpMixins, showErrorsMixins ],
  components: { OrderStateButton, PositionsTable, TableCustomerData, AvatarsCell,CarDataLink },
  props: {
    user: Object,
  },
	data() {
		return {
      openedItemsIds: [],
      fieldsExpand: 'customer,' +
          'customer.customerStatus,' +
          'contactPerson.customerStatus,' +
          'creator,archivedBy,' +
          'car,' +
          'change_history_counts,' +
          'parentOffer,' +
          'parentOrder,' +
          'unpaid,' +
          'masters,' +
          'invoiceNumberAndUid',
      apiLink: 'order',
      routePath: '/archived-orders',
      sort: {
        column: 'archived_at',
        direction: '-',
      },
		}
	},
  watch: {
    '$store.state.updatedOrderId'(orderId) {
      if (!orderId) return

      this.updateOrder(orderId)

      setTimeout(() => {
        this.$store.commit('changeUpdatedOrderId', null)
      }, 100)
    },
  },
  mounted() {
    this.scrollToTop()
    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) return false
    this.checkRouteQuery()
    this.getPageData()
  },
  methods: {
    getPageData() {
      this.getItems()
    },
    async updateOrder(id) {
      if (!id) return false
      this.updatedItemId = id
      let indexToReplace = this.items?.findIndex(item => item.id === id)
      if (indexToReplace > -1) {
        const itemData = await this.getOrderById(id)
        if (!itemData) return

        this.items[indexToReplace] = itemData
        this.updatedItemId = id
      }
    },
    async getOrderPositions(item) {
      // if (!this.$store.getters.isUserReady || !item?.id) return
      if (!this.userCanGetData) return
      const itemId = item.id
      let foundItem = this.items?.find(item => item.id === itemId)
      if (!foundItem) return
      foundItem.positionsAreLoading = true
      const ctrl = this.setController?.('orderPositions')
      try {
        const { status, data } = await httpClient({
          // const { data } = await httpClient({
          url: 'order/get-by-id',
          method: 'post',
          params: {
            // page: this.pagination.currentPage,
            // 'per-page': this.pagination.perPage,
            // sort: this.sort.direction + this.sort.column,
            // search: this.processedSearchValue ?? null,
            expand: 'customer,customer.customerStatus,creator,archivedBy,positions,positions.historyCount,positions.requestStateHistoryCount,consumable,car,change_history_counts,parentOffer,parentOrder,unpaidSum,masters,invoiceNumber',
          },
          data: { id: itemId },
          signal: ctrl?.signal
        })

        if (status === 200 && data && data.success && data.item) {
          foundItem.positions = data.item.positions
          foundItem.consumable = data.item.consumable
          foundItem.change_history_counts = data.item.change_history_counts
          foundItem.positionsLoaded = true
        }
      } catch(err) {
        this.showError(err)
      } finally {
        if (this._abortors?.orderPositions === ctrl) {
          this._abortors.orderPositions = null
        }
        foundItem.positionsAreLoading = false
      }
    },
    async toggleWarning(item) {
      if (!item) return false
      item.is_warning = !item.is_warning
      try {
        const { status } = await httpClient.post('order/toggle-warning', { id: item.id, state: item.is_warning })
        if (status !== 200) {
          item.is_warning = !item.is_warning
        }
      } catch(err) {
        item.is_warning = !item.is_warning
        this.showError(err)
      }
    },
    async getItems(showSpinner = false) {
      if (!this.userCanGetData || this.fetchInFlight) return
      this.$store.commit('SET_FETCH_IN_FLIGHT', true)

      this.dataTableIsLoading = true
      if (showSpinner) {
        this.isLoading = true
      }

      const ctrl = this.setController?.('archivedOrders')
      try {
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          params: {
            archived: 1,
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.processedSearchValue ?? null,
            expand: this.fieldsExpand,
          },
          signal: ctrl.signal,
        })
        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }

          this.items = data

          if (this.openedItemsIds.length) {
            this.items.forEach(item => {
              if (this.openedItemsIds.indexOf(item.id) !== -1) {
                item.showPositionsTable = true
              }
            })
          }

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        this.showError?.(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.isLoading = false
        this.dataTableIsLoading = false
        this.disableEditButton = false
        if (this._abortors?.archivedOrders === ctrl) {
          this._abortors.archivedOrders = null
        }
      }
    },
	},
  computed: {
    actionButtonsCount() {
      return 1
    },
  }
}
</script>
